import ReactGA from 'react-ga';
import dataModel from '../../models/DataModel';
import OfflineQueue from '../../dao/queue/OfflineQueue';

var _gaInitialized = false;

export class Analytics {
  constructor(opts = {}) {
    if (!_gaInitialized && process && process.env && process.env.REACT_APP_GA_KEY) {
      _gaInitialized = ReactGA.initialize(process.env.REACT_APP_GA_KEY);
    }
    this.currentRole = opts ? opts.currentRole : null;
    this.projectTemplate = opts && opts.projectTemplate ? opts.projectTemplate : null;
    this.closeoutTemplateType = opts && opts.closeoutTemplateType ? opts.closeoutTemplateType : 'Standard';
    this.initialized = _gaInitialized;
    this.offlineQueue = OfflineQueue;
  }

  /**
   * Set Page
   * @param { String } pageName - Name/Path of page
   * Sets the page so all subsequent events will be associated with page path
   **/
  setPage(pageName) {
    if (this.initialized) {
      ReactGA.set({ page: pageName });
    }
  }

  /**
   * Page View
   * @param { String } pageName - Name/Path of page
   * Records an instance of a user visiting the page/path
   **/
  pageView(pageName) {
    if (this.initialized) {
      ReactGA.pageview(pageName);
    }
  }

  /**
   * Modal View
   * @param { String } modalName - Name/Path of modal
   * Records an instance of a user opening a modal
   **/
  modalView(modalName) {
    if (this.initialized) {
      ReactGA.modalview(modalName);
    }
  }

  /**
   * Event
   * @param { String } category - Category types : Session, User, Project, Task, ProjectTemplate, Directory, Company
   * @param { String } action - Action samples : create, update, delete, login, assign
   * @param { String } label - Label : String description of action taken (optional)
   * @param { Integer } value - Value : Int value associated with action (optional)
   * @param { String } userTaskAssignment - User Task Assignment : Used to track what actions the users will be using (assigned to, reviewer, approver, notifier) (optional)
   **/
  event(category, action, label, value, userTaskAssignment) {
    if (this.initialized) {
      if (dataModel.isOffline) {
        this.offlineQueue.addQueueItem('analytics', category, { action: action, label: label, value: value, userTaskAssignment: userTaskAssignment });
      }
      else {
        this.setDetails();
        if (userTaskAssignment) {
          this.taskAssignmentRole(userTaskAssignment);
        }
        ReactGA.event({
          category: category,
          action: action,
          label: label,
          value: value,
        });
      }
    }
  }

  /**
  * @param { String } category - Category types : Session, User, Project, Task, ProjectTemplate, Directory, Company
  * @param { String } action - Action samples : create, update, delete, login, assign
  * @param { String } label - Label : String description of action taken (optional)
  * @param { Integer } value - Value : Int value associated with action (optional)
  * @param { Object } customDimensions - Custom dimensions object, i.e. {dimension1: 'val1', ..}, default value: {}
  * @param { String } userTaskAssignment - User Task Assignment : Used to track what actions the users will be using (assigned to, reviewer, approver, notifier) (optional)
  **/
  eventWithCustomDimensions(category, action, label, value, customDimensions = {}, userTaskAssignment) {
    if (this.initialized) {
      this.setDetails();
      if (userTaskAssignment) {
        this.taskAssignmentRole(userTaskAssignment);
      }
      const baseEventObject = {
        hitType: 'event',
        eventCategory: category,
        eventAction: action,
        eventLabel: label,
        eventValue: value,
      };
      const eventObjectWithDimensions = Object.assign({}, baseEventObject, customDimensions);
      ReactGA.ga('send', eventObjectWithDimensions);
    }
  }

  // event('Quick Assign', '', 'click', null, userTaskAssignment)
  /**
   * Exception Report
   * @param { String } description - Description of exception or error
   * @param { Boolean } fatal - Send true if exception was fatal
   * Records an instance of an error or warning
   **/
  exceptionReport(description, fatal) {
    if (this.initialized) {
      ReactGA.exception({
        description: description,
        fatal: fatal,
      });
    }
  }

  /**
   *
   * Helper Functions
   * When setting dimensions, a string must be sent up as the value
   **/

  setDetails() {
    this.currentUser();
    this.currentCompany();
    this.primaryCompany();
    this.userRole();
    this.mobileEnv();
    this.setProjectTemplate();
    this.setCloseoutTemplateType();
  }

  currentUser() {
    let userId = localStorage.loggedin_user_id
      ? localStorage.loggedin_user_id
      : null;
    if (userId) {
      ReactGA.ga('set', 'dimension7', userId);
    }
  }

  currentCompany() {
    let companyId = localStorage.current_company_id
      ? localStorage.current_company_id
      : null;
    if (companyId) {
      ReactGA.ga('set', 'dimension1', companyId);
    }
  }

  setProjectTemplate() {
    if (this.projectTemplate) {
      ReactGA.ga('set', 'dimension8', this.projectTemplate);
    }
  }

  setCloseoutTemplateType() {
    if (this.closeoutTemplateType) {
      ReactGA.ga('set', 'dimension9', this.closeoutTemplateType);
    }
  }

  primaryCompany() {
    let primaryCompanyId = localStorage.primary_company_id
      ? localStorage.primary_company_id
      : null;
    if (primaryCompanyId) {
      ReactGA.ga('set', 'dimension6', primaryCompanyId);
    }
  }

  userRole() {
    let userRole = this.currentRole ? this.currentRole.description : null;
    if (userRole) {
      ReactGA.ga('set', 'dimension2', userRole);
    }
  }

  taskAssignmentRole(role) {
    ReactGA.ga('set', 'dimension3', role);
  }

  mobileEnv() {
    if (document.URL.indexOf('file://') == 0 || window.cordova) {
      ReactGA.set({ checkProtocolTask: null, checkStorageTask: null });
    }
  }
}

export default Analytics;



// WEBPACK FOOTER //
// ./src/helpers/analytics/analytics.js